/**
 * @file Integration API functions for managing external integrations (e.g., Cliniko)
 * @module api/integrationApi
 */

import { ENV } from '../../config/runtimeEnv';
import { Integration, IntegrationCreateRequest, IntegrationUpdateRequest, TestConnectionResponse, SyncStatusResponse } from "../../types/admin/IntegrationTypes";
import { Company } from "../../types/admin/CompanyTypes";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/**
 * Fetch all integrations for a company
 */
export const fetchIntegrations = async (companyId: number): Promise<Integration[]> => {
	const response = await http.get(`${API_BASE_URL}/admin/integrations`, {
		params: { company_id: companyId },
	});
	return response.data.data;
};

/**
 * Fetch a single integration by ID
 */
export const fetchIntegration = async (id: number, companyId: number): Promise<Integration> => {
	const response = await http.get(`${API_BASE_URL}/admin/integrations/${id}`, {
		params: { company_id: companyId },
	});
	return response.data.data;
};

/**
 * Create a new integration
 */
export const createIntegration = async (data: IntegrationCreateRequest): Promise<Integration> => {
	const response = await http.post(`${API_BASE_URL}/admin/integrations`, data);
	return response.data.data;
};

/**
 * Update an existing integration
 */
export const updateIntegration = async (
	id: number,
	data: IntegrationUpdateRequest
): Promise<Integration> => {
	const response = await http.put(`${API_BASE_URL}/admin/integrations/${id}`, data);
	return response.data.data;
};

/**
 * Delete an integration
 */
export const deleteIntegration = async (id: number, companyId: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/admin/integrations/${id}`, {
		params: { company_id: companyId },
	});
};

/**
 * Test integration connection
 */
export const testIntegrationConnection = async (
	id: number,
	companyId: number
): Promise<TestConnectionResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/admin/integrations/${id}/test`,
		{ company_id: companyId }
	);
	return response.data;
};

export type ManualSyncType = "patients" | "practitioners" | "all";

/** 202 from "Sync now": the run is queued and its counts arrive on sync-status. */
export interface SyncQueuedResponse {
	success: boolean;
	message: string;
	sync_type: ManualSyncType;
}

/**
 * Queue a manual sync. A 409 `conflict` with `details.reason: "sync_in_progress"` means a run is already queued or running.
 */
export const syncIntegration = async (
	id: number,
	companyId: number,
	syncType: ManualSyncType = "all"
): Promise<SyncQueuedResponse> => {
	const response = await http.post(`${API_BASE_URL}/admin/integrations/${id}/sync`, {
		company_id: companyId,
		sync_type: syncType,
	});
	return response.data;
};

/**
 * Get sync status for an integration
 */
export const getIntegrationSyncStatus = async (
	id: number,
	companyId: number
): Promise<SyncStatusResponse> => {
	const response = await http.get(`${API_BASE_URL}/admin/integrations/${id}/sync-status`, {
		params: { company_id: companyId },
	});
	return response.data.data;
};


/**
 * Fetch a single integration by ID
 */
export const checkFinanceIntegrationForCompany = async (): Promise<{isActive: Boolean, publicKey?: string}> => {
	const response = await http.get(`${API_BASE_URL}/public/user/integrations/stripe`);
	return {
		isActive: response.data.success, 
		publicKey: response.data.stripe_public_key,
	};
};